import { nextTick } from "vue";

/**
 * Set localStorage
 */
export const setStore = (name, content) => {
  if (!name) return
  if (typeof content !== 'string') {
    content = JSON.stringify(content)
  }
  return window.localStorage.setItem(name, content)
}
/**
  * Get localStorage
*/
export const getStore = (name) => {
  if (!name) return
  return JSON.parse(window.localStorage.getItem(name))
}
/**
 * Clear localStorage
*/
export const removeItem = (name) => {
  if (!name) return
  return window.localStorage.removeItem(name)
}
/**
 * Validate Email address
 */
export const isValidEmail = (value) => {
  return value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,64}$/i.test(value) ? false : true
}

export const formatPrice = (price) => {
  price = parseFloat(price).toFixed(2);
  return '$ ' + price.toLocaleString('en-US', {minimumFractionDigits: 2});
}

export const MEGAPOS_ORDER = 1;
export const ONLINE_ORDER = 2;
export const KIOSK_ORDER = 3;

export const CLOUD_INVENTORY = 1;

export const REGULAR_TOPUP = 1;
export const PROMOTIONAL_TOPUP = 2;

// The clock every stored date belongs to: the API runs on Asia/Singapore
// (config/app.php) and hands dates back as plain 'Y-m-d H:i:s' strings in that
// zone. Screens that let the merchant pick a future moment have to use the same
// clock, or a browser in another timezone books a time the API reads as past.
export const SHOP_TIMEZONE = "Asia/Singapore";

/**
 * M897 — bring the first still-missing compulsory field back into view when a
 * form refuses to submit.
 *
 * Every create/edit screen in this portal flags a failed field the same way:
 * `processValidate()` flips a `fieldRequired.*` switch and the template renders
 * an element carrying the `is-invalid` class. That class is therefore the
 * anchor here — the first visible one in document order is the first problem on
 * the form. Screens like Create Item or Voucher Set run well past one viewport,
 * so without this the merchant presses Save at the bottom of the page and sees
 * nothing happen at all.
 *
 * `root` (optional): the element to search, for a form whose error popup opens on top of it
 * (the popup would otherwise count as "the modal on top" and hold no error).
 */
export const scrollToFirstInvalid = (root = null) => {
  nextTick(() => {
    // Whatever was ringed last time answered the previous submit, not this one.
    clearHighlights();

    // A modal sits on top of the screen that opened it, and that screen may
    // still be showing errors of its own from an earlier attempt. Search only
    // inside the modal on top, or the page would scroll behind the dialog.
    const openModals = Array.from(
      document.querySelectorAll(".modal.show")
    ).filter(isFieldVisible);
    const scope = root || (openModals.length ? openModals[openModals.length - 1] : document);

    const firstError = Array.from(scope.querySelectorAll(".is-invalid")).find(
      isFieldVisible
    );
    if (!firstError) return;

    firstError.scrollIntoView({ behavior: "smooth", block: "center" });

    const owner = controlFor(firstError);
    if (!owner) return;

    if (owner.matches(FILE_FIELD) && !isFieldVisible(owner)) {
      // A file input is normally hidden behind a button or an upload area — ring
      // whatever the merchant actually clicks, since the input itself cannot be
      // focused or even seen.
      const standIn = standInFor(owner);
      if (standIn) highlightControl(standIn);
      return;
    }

    const dropdown = owner.closest(".multiselect");
    if (dropdown) {
      // A dropdown opens its option list the moment it takes focus, which would
      // bury the message we just scrolled to. Ring it instead, so the merchant
      // still sees which field stopped the submit.
      highlightControl(dropdown);
    } else if (owner.matches(FOCUSABLE_FIELD)) {
      // preventScroll: the smooth scroll above is already under way; letting the
      // focus scroll too snaps the page there instantly and then drifts.
      owner.focus({ preventScroll: true });
    }
  });
};

const ERROR_HIGHLIGHT = "field-needs-attention";

const clearHighlights = () =>
  Array.from(document.querySelectorAll("." + ERROR_HIGHLIGHT)).forEach((el) =>
    el.classList.remove(ERROR_HIGHLIGHT)
  );

const highlightControl = (el) => {
  el.classList.add(ERROR_HIGHLIGHT);
  // It has done its job once the merchant reaches the field; leaving the ring on
  // while they pick an option would just read as a second, stale error.
  const clear = () => el.classList.remove(ERROR_HIGHLIGHT);
  el.addEventListener("pointerdown", clear, { once: true });
  el.addEventListener("focusin", clear, { once: true });
};

// Covers display:none, an ancestor that is hidden, and the `d-none` error spans
// some screens toggle by hand — all of them report no client rects.
const isFieldVisible = (el) => el.getClientRects().length > 0;

const FILE_FIELD = 'input[type="file"]';

const FOCUSABLE_FIELD =
  'input:not([type="hidden"]):not([disabled]):not([readonly]), ' +
  "textarea:not([disabled]):not([readonly]), select:not([disabled])";

const controlFor = (el) => {
  if (el.matches(FOCUSABLE_FIELD)) return el;
  // The error text usually sits next to its input, but on the table layouts the
  // input is a cell away and in the modals the message is a sibling of the whole
  // row, so widen the search one ancestor at a time until a control turns up.
  // Dropdowns are collected too even though they are never focused: they still
  // mark where one field ends and the next begins.
  let scope = el.parentElement;
  for (let depth = 0; scope && depth < 3; depth++, scope = scope.parentElement) {
    const controls = Array.from(
      scope.querySelectorAll(FOCUSABLE_FIELD + ", .multiselect")
    ).filter((c) => isFieldVisible(c) || c.matches(FILE_FIELD));
    if (!controls.length) continue;
    // A message always follows the control it belongs to, so the one to blame is
    // the LAST control above it — not the first in the container, or a wide
    // container hands back the field at the top of the form, which the merchant
    // has usually already filled in.
    const above = controls.filter(
      (c) => c.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    return above.length ? above[above.length - 1] : controls[0];
  }
  return null;
};

// What to ring in place of a hidden file input: the button beside it, or failing
// that the upload area it is buried in.
const standInFor = (fileInput) => {
  let scope = fileInput.parentElement;
  for (let depth = 0; scope && depth < 3; depth++, scope = scope.parentElement) {
    const button = Array.from(scope.querySelectorAll("button, .btn")).find(
      isFieldVisible
    );
    if (button) return button;
    if (isFieldVisible(scope)) return scope;
  }
  return null;
};

// "Deducted Bonus" and "Deducted Cashback" are now one "Deducted Cashback/Bonus"
// column. Column lists saved in localStorage still carry the old keys.
export const mergeDeductedBonusCashbackColumns = (columns, bonusKey, cashbackKey, mergedKey) => {
  const hadOldKey = columns.includes(bonusKey) || columns.includes(cashbackKey);
  const result = columns.filter((column) => column !== bonusKey && column !== cashbackKey);
  if (hadOldKey && !result.includes(mergedKey)) {
    result.push(mergedKey);
  }
  return result;
};
